import angular from "angular";
import { UserDefinedRow, Configs, RowsUsecaseLogic } from "@salaxy/core";

import {
  UiHelpers,
} from "../../services";

/**
 * Interface representing the structure of row type documentation
 * @interface RowTypeDocumentation
 */
interface RowTypeDocumentation {
  /** The type identifier for the row */
  type: string;
  /** Optional description of the row type */
  description?: string;
  /** Optional array of sample configurations */
  samples?: Array<{
    /** Description of the sample */
    description: string;
    /** The specific kind/variant of the sample */
    kind: string;
    /** Code sample */
    code: string;
  }>;
}

/**
 * Controller responsible for handling the documentation of calculation rows.
 * Implements the Angular IController interface.
 */
export class CalcRowDocumentationController implements angular.IController {
  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = [
    "UiHelpers",
    "$timeout",
  ];

  /** Currently edited row */
  public row: UserDefinedRow;

  /**
   * An object that holds example data for the CalcRowDocumentationController.
   */
  public allExamples: RowTypeDocumentation[] = [];

  public selectedKind: string;

  private _isLoading: boolean;

  public tabOptions: { value: string; text: number }[] = [];

  /**
   * Constructor to initialize the controller with dependencies.
   * @param uiHelpers - Helper utilities for UI operations.
   * @param $timeout - Angular's timeout service.
   */
  constructor(private uiHelpers: UiHelpers, private $timeout: angular.ITimeoutService) {}

  /**
   * Lifecycle hook that is called after the controller's data-bound properties have been initialized.
   * Fetches example data from the CDN and updates the loading state.
   */
  public $onInit() {
    this._isLoading = true;
    this.fetchExamplesFromCdn().then(result => {
      this.allExamples = result;
      this.generateExampleLocally();
      this._isLoading = false;
      this.$timeout();
    });
  }

  /**
   * Determines if the loader should be displayed.
   * @returns {boolean} True if the loader should be shown, false otherwise.
   */
  public showLoader(): boolean {
    return this._isLoading;
  }

  /**
   * Fetches a file from the CDN.
   */
  public async fetchExamplesFromCdn(): Promise<RowTypeDocumentation[]> {
    const examplesCdnUrl = Configs.current.isTestData ? "https://cdn.salaxy.com/docs/row-types/samples/test/examples.json" : "https://cdn.salaxy.com/docs/row-types/samples/prod/examples.json";
    try {
      const response = await fetch(examplesCdnUrl);
      if (!response.ok) {
        console.error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data.examples;
    } catch (error) {
      console.error("Error fetching file from CDN:", error);
      return [];
    }
  }

  /**
   * Gets the description text for the current example type.
   */
  public get example() {
    return this.allExamples?.find(x => x.type === (this.row as any)?.config.type);
  }

  /**
   * Generates a local example for the current row type and selected kind.
   * This method:
   * 1. Checks if there is a valid row to generate an example for
   * 2. Determines if the row has a kind configuration
   * 3. If conditions are met:
   * - Finds or creates an example for the current row type
   * - Removes any existing generated examples
   * - Creates a new sample with generated code
   * - Adds the sample to the examples collection
   * 4. Triggers Angular digest cycle via $timeout
   */
  public generateExampleLocally() {
    if (!this.row) {
      return;
    }
    const rowConfig = (this.row as any).config;
    const rowType = rowConfig.type;
    const hasKind = rowConfig?.kind?.name;

    if (!hasKind && !this.selectedKind || hasKind && this.selectedKind) {
      const example = this.allExamples.find(x => x.type === rowType);
      if (example) {
        example.samples = example.samples.filter(x => x.description !== "Generated example");
      }
      const newSample = {
        code: this.generateCodeExample(),
        description: "Generated example",
        kind: hasKind ? this.selectedKind : null
      };

      if (example) {
        example.samples.push(newSample);
      } else {
        const description = `Examples for ${this.row.rowType}`;

        this.allExamples.push({
          type: rowType,
          description,
          samples: [newSample]
        });
      }
    }
    this.$timeout();
  }

  /**
   * Gets the code samples for the current row type and kind.
   * Results are cached based on the type and kind combination.
   */
  public get exampleSamples() {
    return this.uiHelpers.cache(this, "exampleSamples", () => {
      this.tabOptions = [];
      const example = this.allExamples.find(x => x.type === (this.row as any)?.config.type);
      const hasKind = (this.row as any).config?.kind?.name;
      if (hasKind && !this.selectedKind) {
        return [];
      }

      const filteredSamples = example?.samples?.filter(x => !hasKind || x.kind === this.selectedKind) ?? [];
      for (let i = 1; i < filteredSamples.length+1; i++) {
        this.tabOptions.push({ value: "tab" + i, text: i });
      }

      return filteredSamples;
    }, () => `${(this.row as any)?.config.type}-${this.selectedKind}`);
  }

  private generateCodeExample(): string {
    const rowConfig = (this.row as any).config;
    const hasKind = rowConfig?.kind?.name;
    if (!hasKind) {
      return `import {CalculatorLogic, CalculationRowType, RowsUsecaseLogic} from "@salaxy/core"; \n//Make an entry for row\nconst row = RowsUsecaseLogic.getBlankUsecaseRow(CalculationRowType.${this.row.rowType.substr(0, 1).toUpperCase() + this.row.rowType.substr(1)});\n// Add created row to new calculation\nconst calculation = CalculatorLogic.getBlank();\ncalculation.rows.push(row);`;
    } else {
      const data = RowsUsecaseLogic.getUsecaseExampleData(this.row.rowType as any, this.selectedKind);
      const dataWithoutKind = Object.assign({}, data);
      delete dataWithoutKind.kind;
      const dataString = JSON.stringify(dataWithoutKind, null, 2).replace(/^{/, "{\n  \"kind\": " + rowConfig.kind.name + "." + this.selectedKind.substr(0, 1).toUpperCase() + this.selectedKind.substr(1) + ",");
      return `import {CalculatorLogic, CalculationRowType, RowsUsecaseLogic, ${rowConfig.kind.name}} from "@salaxy/core"; \n//Make an entry for row\nconst row = RowsUsecaseLogic.getBlankUsecaseRow(CalculationRowType.${this.row.rowType.substr(0, 1).toUpperCase() + this.row.rowType.substr(1)});\nrow.data = ${dataString} \n// Add created row to new calculation\nconst calculation = CalculatorLogic.getBlank();\ncalculation.rows.push(row);`;
    }
  }
}